import React, { useState } from 'react';
import { Volume2, CheckCircle, XCircle, Mic, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSounds } from '@/hooks/useSounds';

export default function SimpleSpeaking({ 
  instruction = '',
  answer = '',
  items = [],
  onAnswer,
  disabled = false 
}) {
  const [showAnswer, setShowAnswer] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [completedItems, setCompletedItems] = useState([]);
  const [userMarkedCorrect, setUserMarkedCorrect] = useState(null);
  const { playCorrect, playIncorrect, playClick } = useSounds();

  // items varsa çoklu mod, yoksa tekli mod
  const isMultiMode = items && items.length > 0;
  const currentItem = isMultiMode ? items[currentItemIndex] : null;
  const currentInstruction = isMultiMode 
    ? (currentItem?.code || currentItem?.instruction || `Soru ${currentItemIndex + 1}`)
    : instruction;
  const currentAnswer = isMultiMode 
    ? (currentItem?.answer || '')
    : answer;

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      try { playClick?.(); } catch(e) { /* ignore */ }
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.9;
      speechSynthesis.speak(utterance);
    }
  };

  const handleShowAnswer = () => {
    setShowAnswer(true);
  };

  const handleSelfEvaluation = (correct) => {
    setUserMarkedCorrect(correct);
    
    if (correct) {
      try { playCorrect?.(); } catch(e) { /* ignore */ }
    } else {
      try { playIncorrect?.(); } catch(e) { /* ignore */ }
    }

    if (isMultiMode) {
      // Çoklu modda sonraki soruya geç
      const newCompleted = [...completedItems, { index: currentItemIndex, correct }];
      setCompletedItems(newCompleted);

      setTimeout(() => {
        if (currentItemIndex < items.length - 1) {
          setCurrentItemIndex(prev => prev + 1);
          setShowAnswer(false);
          setUserMarkedCorrect(null);
        } else {
          // Tüm sorular tamamlandı
          const correctCount = newCompleted.filter(c => c.correct).length;
          const passed = correctCount >= items.length * 0.6; // %60 geçme notu
          onAnswer?.(passed, items.length - correctCount);
        }
      }, 1000);
    } else {
      // Tekli modda direkt tamamla
      setTimeout(() => {
        onAnswer?.(correct, correct ? 0 : 1);
      }, 1000);
    }
  };

  const handleRetry = () => {
    setShowAnswer(false);
    setUserMarkedCorrect(null);
  };

  // Instruction veya answer yoksa hata göster
  if (!currentInstruction && !currentAnswer) {
    return (
      <div className="w-full max-w-2xl mx-auto text-center py-8">
        <p className="text-gray-500">Konuşma pratiği verisi yüklenemedi.</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Progress - çoklu modda */}
      {isMultiMode && (
        <div className="mb-4 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            Soru {currentItemIndex + 1} / {items.length}
          </span>
          <div className="flex gap-1">
            {items.map((_, idx) => (
              <div 
                key={idx}
                className={cn(
                  "w-2 h-2 rounded-full",
                  idx < currentItemIndex 
                    ? (completedItems.find(c => c.index === idx)?.correct ? 'bg-green-500' : 'bg-red-400')
                    : idx === currentItemIndex 
                      ? 'bg-blue-500' 
                      : 'bg-gray-200'
                )}
              />
            ))}
          </div>
        </div>
      )}

      {/* Main Card */}
      <div className="bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-4 text-white">
          <div className="flex items-center gap-2">
            <Mic className="w-5 h-5" />
            <span className="font-semibold">Konuşma Pratiği</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Instruction */}
          <div className="mb-6 text-center">
            <p className="text-gray-600 text-sm mb-2">
              {isMultiMode ? 'Aşağıdaki kodu fonetik alfabeyle söyleyin:' : 'Söylemeniz gereken:'}
            </p>
            <div className="bg-gray-50 rounded-xl p-4 border">
              <p className="text-xl font-bold text-gray-800">{currentInstruction}</p>
            </div>
          </div>

          {/* Show Answer Button */}
          {!showAnswer && (
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-4">
                Cevabınızı sesli söyleyin, ardından kontrol edin.
              </p>
              <button
                onClick={handleShowAnswer}
                disabled={disabled}
                className="px-6 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                Cevabı Göster
              </button>
            </div>
          )}

          {/* Answer Display */}
          {showAnswer && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-sm text-green-600 mb-2 font-medium">Doğru Cevap:</p>
                <div className="flex items-center justify-between">
                  <p className="text-lg font-semibold text-green-800">{currentAnswer}</p>
                  <button
                    onClick={() => speakText(currentAnswer)}
                    className="p-2 bg-green-100 rounded-lg hover:bg-green-200 transition-colors"
                    title="Dinle"
                  >
                    <Volume2 className="w-5 h-5 text-green-600" />
                  </button>
                </div>
              </div>

              {/* Self Evaluation */}
              {userMarkedCorrect === null && (
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-3">Cevabınız doğru muydu?</p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={() => handleSelfEvaluation(false)}
                      disabled={disabled}
                      className="flex items-center gap-2 px-6 py-3 bg-red-100 text-red-700 rounded-xl font-medium hover:bg-red-200 transition-colors disabled:opacity-50"
                    >
                      <XCircle className="w-5 h-5" />
                      Yanlış
                    </button>
                    <button
                      onClick={() => handleSelfEvaluation(true)}
                      disabled={disabled}
                      className="flex items-center gap-2 px-6 py-3 bg-green-100 text-green-700 rounded-xl font-medium hover:bg-green-200 transition-colors disabled:opacity-50"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Doğru
                    </button>
                  </div>
                </div>
              )}

              {/* Result Feedback */}
              {userMarkedCorrect !== null && (
                <div className={cn(
                  "text-center p-4 rounded-xl",
                  userMarkedCorrect ? "bg-green-100" : "bg-amber-100"
                )}>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    {userMarkedCorrect ? (
                      <>
                        <CheckCircle className="w-6 h-6 text-green-600" />
                        <span className="font-bold text-green-700">Harika!</span>
                      </>
                    ) : (
                      <>
                        <RotateCcw className="w-6 h-6 text-amber-600" />
                        <span className="font-bold text-amber-700">Tekrar deneyin</span>
                      </>
                    )}
                  </div>
                  {!userMarkedCorrect && !isMultiMode && (
                    <button
                      onClick={handleRetry}
                      className="text-sm text-amber-600 underline hover:no-underline"
                    >
                      Tekrar Dene
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tip */}
      <div className="mt-4 text-center text-sm text-gray-500">
        💡 Telaffuzunuzu geliştirmek için cevabı dinleyebilirsiniz
      </div>
    </div>
  );
}
